import { HdBodygraphChartPreset, HdBodygraphPlanetaryActivation } from './hd-bodygraph.models';

/**
 * De tweede kant van een cyclusduiding: wat er op de natal chart gelegd wordt.
 * Een momentchart (transit-blok, spec transits 2026-09-26 §2.8) wordt als
 * cyclus behandeld — dezelfde logica van wat het moment met de chart doet —
 * maar draagt enkel een personality-kant (een moment heeft geen design).
 */
export interface HdCycleOverlaySource {
  /** `cycle`: cycle chart (P+D van het cyclusmoment); `moment`: momentchart (enkel P). */
  readonly kind: 'cycle' | 'moment';
  /** Gevalideerde activaties van het moment/cyclusmoment. */
  readonly activations: HdBodygraphPlanetaryActivation[];
  /** Metadata van het moment (Chiron-bron), indien aanwezig. */
  readonly metadata: Record<string, unknown> | null;
}

/** Overlay-bron van een cycle- of momentchart; `null` voor alle andere charts. */
export function resolveHdCycleOverlaySource(
  preset: HdBodygraphChartPreset | null | undefined,
): HdCycleOverlaySource | null {
  if (preset?.cycle != null) {
    return {
      kind: 'cycle',
      activations: filterHdCycleOverlayActivations(preset.cycle.moment.planetaryActivations),
      metadata: preset.cycle.moment.metadata ?? null,
    };
  }
  if (preset?.transit != null) {
    return {
      kind: 'moment',
      activations: filterHdCycleOverlayActivations(preset.transit.personalityActivations)
        .filter(activation => activation.side === 'personality'),
      metadata: preset.transit.momentMetadata ?? null,
    };
  }
  return null;
}

/**
 * Overlay-activaties voor een cycle chart: BEIDE zijden van het cycle-moment
 * (de just-now-transit projecteert bewust alleen personality; een cycle chart
 * legt de volledige gehelen op elkaar — spec cycle charts §10). Zelfde
 * sanity-checks als filterHdJustNowPersonalityActivations, zonder de side-eis.
 */
export function filterHdCycleOverlayActivations(
  activations: readonly HdBodygraphPlanetaryActivation[] | null | undefined,
): HdBodygraphPlanetaryActivation[] {
  const seenIds = new Set<string>();
  return (activations ?? []).filter(activation => {
    if (activation.side !== 'personality' && activation.side !== 'design') return false;
    if (activation.sourceType !== 'planet' || activation.id.trim().length === 0) return false;
    if (!Number.isInteger(activation.gate) || activation.gate < 1 || activation.gate > 64) return false;
    if (!Number.isInteger(activation.planetId) || activation.planetId < 0) return false;
    if (!Number.isInteger(activation.line) || activation.line < 1 || activation.line > 6) return false;
    if (seenIds.has(activation.id)) return false;
    seenIds.add(activation.id);
    return true;
  });
}
